import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// Full-page meeting record.
//
// The agenda is what was planned; discussion points are what happened. They are
// kept apart on purpose, so editing one never rewrites the other.
//
// Every number in the right-hand rail is counted from the points and their
// linked tickets — nothing here is stored as a total, so it cannot drift.

const DONE = ['Completed', 'Closed'];
const CAT = {
  task:          { label:'Task',        bg:'#FDE2E4', fg:'#C0392B' },
  information:   { label:'Information', bg:'#DCE9FB', fg:'#1F6FD0' },
  policy_change: { label:'Policy',      bg:'#E8DFF7', fg:'#6B45B8' },
};
const STATUS_COLORS = {
  'Completed':   { bg:'#D7F2E1', fg:'#1E7C4B' },
  'Closed':      { bg:'#D7F2E1', fg:'#1E7C4B' },
  'In Progress': { bg:'#FDF0CD', fg:'#9A6B0B' },
  'Pending':     { bg:'#FDE2E4', fg:'#C0392B' },
  'Assigned':    { bg:'#DCE9FB', fg:'#1F6FD0' },
};

const initials = n => String(n || '?').split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
// A stable colour per person: the same name always gets the same chip.
const hueOf = n => { let h = 0; for (const ch of String(n || '')) h = (h * 31 + ch.charCodeAt(0)) % 360; return h; };
const Avatar = ({ name, size = 26 }) => (
  <span title={name} style={{ width:size, height:size, borderRadius:'50%', flexShrink:0,
    background:`hsl(${hueOf(name)} 55% 42%)`, color:'#fff', fontSize:size * 0.4, fontWeight:700,
    display:'inline-flex', alignItems:'center', justifyContent:'center' }}>{initials(name)}</span>
);
const Pill = ({ text, bg, fg }) => (
  <span style={{ background:bg, color:fg, padding:'3px 10px', borderRadius:12,
    fontSize:11.5, fontWeight:600, whiteSpace:'nowrap', display:'inline-block' }}>{text}</span>
);
const StatusPill = ({ s }) => {
  const c = STATUS_COLORS[s];
  return c ? <Pill text={s} bg={c.bg} fg={c.fg} /> : <span style={{ color:'var(--text2)' }}>–</span>;
};
// Dates arrive as 'YYYY-MM-DD' strings and are formatted as strings: parsing
// one into a JS Date shifts it a day backwards in IST.
const fmtDate = s => {
  if (!s) return '–';
  const [y, m, dd] = String(s).slice(0, 10).split('-');
  const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][Number(m) - 1];
  return M ? `${dd} ${M} ${y}` : s;
};
const fmt12 = t => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${ap}`;
};
const kb = n => !n ? '' : n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`;

const Card = ({ children, style, pad = 14 }) => (
  <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:10,
    padding:pad, ...style }}>{children}</div>
);

export default function MeetingDetail({ id, onClose, onNew }) {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [carry, setCarry] = useState([]);
  const [itUsers, setItUsers] = useState([]);
  const [tab, setTab] = useState('points');
  const [msg, setMsg] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [view, setView] = useState(null);       // point open in the View modal
  const [editNotes, setEditNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [editAgenda, setEditAgenda] = useState(false);
  const [agendaText, setAgendaText] = useState('');
  const [dlOpen, setDlOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [campuses, setCampuses] = useState([]);

  const load = useCallback(() => {
    api.get(`/containers/${id}`).then(r => {
      setD(r.data); setNotes(r.data.notes || '');
      setAgendaText((r.data.agenda || []).map(a => a.text).join('\n'));
    }).catch(() => {});
    api.get(`/containers/${id}/carry-forward`).then(r => setCarry(r.data)).catch(() => setCarry([]));
  }, [id]);
  useEffect(() => {
    load();
    api.get('/tasks/it-users').then(r => setItUsers(r.data)).catch(() => {});
    api.get('/master/campuses').then(r => setCampuses(r.data.filter(c => c.active))).catch(() => {});
  }, [load]);

  const flash = t => { setMsg(t); setTimeout(() => setMsg(''), 2200); };
  if (!d) return null;

  const canEdit = user && ['admin', 'it_head', 'it_lead'].includes(user.role);
  const items = d.items || [];
  const isInfo = it => it.category === 'information' || it.category === 'policy_change';
  const taskPoints = items.filter(it => !isInfo(it));
  const infoPoints = items.filter(isInfo);
  const allTasks = items.flatMap(it => it.tasks || []);

  // A point's own status if one was set; otherwise it is inferred from the
  // tickets it was fanned out to, so a point never shows a status that
  // contradicts its own tickets.
  const pointStatus = it => {
    if (it.point_status) return it.point_status;
    if (it.closed_at) return 'Completed';
    const ts = it.tasks || [];
    if (!ts.length) return isInfo(it) ? null : 'Pending';
    if (ts.every(t => DONE.includes(t.status))) return 'Completed';
    if (ts.some(t => t.status === 'In Progress')) return 'In Progress';
    return 'Pending';
  };
  const responsibleOf = it => it.responsible_name
    || [...new Set((it.tasks || []).map(t => t.assigned_to_name).filter(Boolean))].join(', ') || '–';
  const dueOf = it => it.due_date_str
    || (it.tasks || []).map(t => t.due_date).filter(Boolean).sort()[0] || null;
  const scopeOf = it => {
    const names = [...new Set((it.tasks || []).map(t => t.campus_name).filter(Boolean))];
    if (!names.length) return 'All Campuses';
    return names.length > 2 ? `${names.length} campuses` : names.join(', ');
  };
  const linkOf = it => {
    const ts = it.tasks || [];
    if (ts.length === 1) return ts[0].task_id;
    if (ts.length > 1) return `${ts.length} tickets`;
    return null;
  };

  // Right rail — every figure counted, none stored.
  const summary = {
    total: items.length,
    tasks: taskPoints.length,
    info: infoPoints.length,
    completed: items.filter(it => ['Completed', 'Closed'].includes(pointStatus(it))).length,
  };
  const taskStat = {
    completed: allTasks.filter(t => DONE.includes(t.status)).length,
    progress:  allTasks.filter(t => t.status === 'In Progress').length,
    pending:   allTasks.filter(t => !DONE.includes(t.status) && t.status !== 'In Progress').length,
  };
  const ack = infoPoints.reduce((a, it) => ({
    done: a.done + Number(it.acks_done || 0), total: a.total + Number(it.acks_total || 0),
  }), { done: 0, total: 0 });

  // Next actions: open tickets, soonest due first.
  const nextActions = allTasks
    .filter(t => !DONE.includes(t.status))
    .sort((a, b) => (a.due_date || '9999') < (b.due_date || '9999') ? -1 : 1)
    .slice(0, 6);

  const saveNotes = async () => {
    try { await api.put(`/containers/${id}/notes`, { notes }); setEditNotes(false); flash('✓ Notes saved'); load(); }
    catch { flash('Could not save notes'); }
  };
  const saveAgenda = async () => {
    try {
      await api.put(`/containers/${id}/agenda`, { agenda: agendaText.split('\n') });
      setEditAgenda(false); flash('✓ Agenda saved'); load();
    } catch { flash('Could not save agenda'); }
  };
  const upload = async file => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) return flash('File must be 15 MB or smaller');
    const data = await new Promise(res => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    try {
      await api.post(`/containers/${id}/attachments`, { orig_name:file.name, mime:file.type, data });
      flash('✓ Attached'); load();
    } catch (e) { flash(e.response?.data?.error || 'Upload failed'); }
  };
  const download = async a => {
    try {
      const r = await api.get(`/containers/attachments/${a.id}/download`, { responseType:'blob' });
      const url = URL.createObjectURL(r.data);
      const el = document.createElement('a'); el.href = url; el.download = a.orig_name; el.click();
      URL.revokeObjectURL(url);
    } catch { flash('Could not download'); }
  };
  const delAttachment = async a => {
    if (!window.confirm(`Remove "${a.orig_name}" from this meeting?`)) return;
    try { await api.delete(`/containers/attachments/${a.id}`); load(); } catch { flash('Could not remove'); }
  };
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/containers/${id}/minutes.pdf`, { responseType:'blob' });
      const url = URL.createObjectURL(r.data);
      const el = document.createElement('a');
      el.href = url; el.download = `${d.meeting_code || 'meeting'}_minutes.pdf`; el.click();
      URL.revokeObjectURL(url); setDlOpen(false);
    } catch { flash('Could not generate the PDF'); }
  };
  // A point can be closed once it is dealt with — closing is what stops it
  // carrying forward into the next meeting of the same kind.
  const closePoint = async (it, closed) => {
    try { await api.patch(`/containers/items/${it.id}/close`, { closed }); load(); }
    catch { flash('Could not update the point'); }
  };
  const delPoint = async it => {
    if (!window.confirm(`Delete "${it.title}"?\n\nAny tickets raised from it are removed too.`)) return;
    try { await api.delete(`/containers/items/${it.id}`); load(); }
    catch { flash('Could not delete'); }
  };
  const exportXlsx = () => {
    const rows = items.map((it, i) => ({
      '#': i + 1, 'Discussion Point': it.title,
      Category: CAT[it.category || 'task']?.label || 'Task',
      'Campus / Scope': scopeOf(it), Responsible: responsibleOf(it),
      'Due Date': dueOf(it) ? fmtDate(dueOf(it)) : '–',
      Status: pointStatus(it) || '–', 'Linked Item': linkOf(it) || '–',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{
      'Meeting ID': d.meeting_code || '–', Title: d.title,
      Date: fmtDate(d.meeting_date_str),
      Time: d.start_time_str ? `${fmt12(d.start_time_str)} – ${fmt12(d.end_time_str)}` : '–',
      Location: d.location || '–', 'Conducted by': d.conducted_by_name || d.owner_name || '–',
      Participants: (d.members || []).map(m => m.full_name).join(', ') || '–',
      Status: d.status || '–',
    }]), 'Meeting');
    if ((d.agenda || []).length)
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        d.agenda.map((a, i) => ({ '#': i + 1, Agenda: a.text }))), 'Agenda');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Discussion Points');
    if (d.notes) XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(
      d.notes.split('\n').map(l => [l])), 'Notes');
    XLSX.writeFile(wb, `${(d.meeting_code || d.title).replace(/[^A-Za-z0-9_-]+/g, '_')}.xlsx`);
    setDlOpen(false);
  };

  const TABS = [
    ['points', `Discussion Points (${items.length})`],
    ['tasks',  `Tasks (${allTasks.length})`],
    ['info',   `Information (${infoPoints.length})`],
    ['follow', `Pending from Earlier Meetings (${carry.length})`],
    ['files',  `Attachments (${(d.attachments || []).length})`],
  ];

  const th = { textAlign:'left', fontSize:11.5, fontWeight:600, color:'var(--text2)',
    textTransform:'uppercase', letterSpacing:'0.04em', padding:'9px 10px',
    borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' };
  const td = { padding:'9px 10px', fontSize:12.5, borderBottom:'1px solid var(--border)', verticalAlign:'middle' };

  return (
    <div>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ fontSize:12, color:'var(--text2)', marginBottom:6 }}>
        <span onClick={onClose} style={{ cursor:'pointer' }}>Meetings</span>
        {' › '}<span onClick={onClose} style={{ cursor:'pointer' }}>{d.title}</span>
        {' › '}<b style={{ color:'var(--text)' }}>{d.meeting_code || `#${d.id}`}</b>
      </div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10, marginBottom:14 }}>
        <h2 style={{ fontSize:22, fontWeight:700 }}>{d.title}</h2>
        <div className="flex-gap" style={{ gap:8, position:'relative' }}>
          {onNew && <button className="btn btn-primary btn-sm" onClick={onNew}>+ New Meeting</button>}
          {canEdit && <button className="btn btn-secondary btn-sm" onClick={() => setEditOpen(true)}>✎ Edit Meeting</button>}
          <button className="btn btn-secondary btn-sm" onClick={() => setDlOpen(o => !o)}>⬇ Download ▾</button>
          {dlOpen && (
            <div style={{ position:'absolute', right:0, top:34, zIndex:60, background:'var(--bg2)',
              border:'1px solid var(--border)', borderRadius:8, padding:6, minWidth:200,
              display:'flex', flexDirection:'column', gap:4,
              boxShadow:'0 6px 20px rgba(0,0,0,0.15)' }}>
              <button className="btn btn-secondary btn-sm" style={{ width:'100%', justifyContent:'flex-start' }}
                onClick={downloadPdf}>📄 Minutes as PDF</button>
              <button className="btn btn-secondary btn-sm" style={{ width:'100%', justifyContent:'flex-start' }}
                onClick={exportXlsx}>📊 Minutes as .xlsx</button>
            </div>
          )}
          <button className="btn btn-secondary btn-sm" onClick={onClose}>✕ Close</button>
        </div>
      </div>
      {msg && <div style={{ marginBottom:10, fontSize:12.5, color:'var(--accent)' }}>{msg}</div>}

      {/* Unfinished business is the first thing a meeting should deal with, so
          it is stated here rather than left behind a tab nobody clicks. */}
      {carry.length > 0 && tab !== 'follow' && (
        <div style={{ background:'#FDF0CD', border:'1px solid #E8CE86', borderRadius:8,
          padding:'10px 12px', marginBottom:12, display:'flex', alignItems:'center',
          gap:10, flexWrap:'wrap' }}>
          <span style={{ fontSize:13, color:'#7A5300' }}>
            <b>{carry.length} point{carry.length > 1 ? 's' : ''}</b> from earlier meetings {carry.length > 1 ? 'are' : 'is'} still pending.
          </span>
          <button className="btn btn-secondary btn-sm" onClick={() => setTab('follow')}>Review them</button>
        </div>
      )}

      {/* ── Meeting card / Agenda / Notes ───────────────────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:12, marginBottom:14 }}>
        <Card>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}><tbody>
            {[['Meeting ID', d.meeting_code || `#${d.id}`],
              ['Date & Time', `${fmtDate(d.meeting_date_str)}${d.start_time_str ? `, ${fmt12(d.start_time_str)}${d.end_time_str ? ` – ${fmt12(d.end_time_str)}` : ''}` : ''}`],
              ['Location', d.location || '–'],
              ['Conducted by', d.conducted_by_name || d.owner_name || '–'],
              ['Meeting Type', d.meeting_kind === 'it_head' ? 'IT Head Meeting' : d.meeting_kind === 'it_lead' ? 'IT Lead Meeting' : '–'],
            ].map(([k, v]) => (
              <tr key={k}>
                <td style={{ color:'var(--text2)', padding:'4px 0', width:120 }}>{k}</td>
                <td style={{ padding:'4px 0 4px 6px' }}>: {v}</td>
              </tr>
            ))}
            <tr>
              <td style={{ color:'var(--text2)', padding:'4px 0', verticalAlign:'top' }}>Participants</td>
              <td style={{ padding:'4px 0 4px 6px' }}>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, alignItems:'center' }}>
                  {(d.members || []).length
                    ? d.members.map(m => (
                        <span key={m.user_id} style={{ display:'inline-flex', alignItems:'center', gap:5,
                          background:'var(--bg3)', borderRadius:14, padding:'2px 9px 2px 2px', fontSize:11.5 }}>
                          <Avatar name={m.full_name} size={22} />{m.full_name}
                        </span>))
                    : <span style={{ color:'var(--text2)' }}>: –</span>}
                </div>
              </td>
            </tr>
            <tr>
              <td style={{ color:'var(--text2)', padding:'6px 0' }}>Meeting Status</td>
              <td style={{ padding:'6px 0 4px 6px' }}>
                <StatusPill s={d.status === 'Closed' ? 'Completed' : d.status || 'Pending'} />
              </td>
            </tr>
          </tbody></table>
        </Card>

        <Card>
          <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <b style={{ fontSize:14 }}>Agenda</b>
            {canEdit && !editAgenda && <button className="btn btn-secondary btn-sm" onClick={() => setEditAgenda(true)}>Edit</button>}
          </div>
          {editAgenda ? (
            <>
              <textarea value={agendaText} onChange={e => setAgendaText(e.target.value)} rows={8}
                placeholder="One agenda point per line" style={{ width:'100%', fontSize:12.5 }} />
              <div className="flex-gap" style={{ gap:6, marginTop:6 }}>
                <button className="btn btn-primary btn-sm" onClick={saveAgenda}>Save</button>
                <button className="btn btn-secondary btn-sm" onClick={() => { setEditAgenda(false); setAgendaText((d.agenda||[]).map(a=>a.text).join('\n')); }}>Cancel</button>
              </div>
            </>
          ) : (d.agenda || []).length ? (
            <ol style={{ margin:0, paddingLeft:0, listStyle:'none' }}>
              {d.agenda.map((a, i) => (
                <li key={a.id} style={{ display:'flex', gap:9, alignItems:'flex-start', padding:'4px 0', fontSize:12.5 }}>
                  <span style={{ width:19, height:19, flexShrink:0, borderRadius:5, background:'var(--bg3)',
                    color:'var(--text2)', fontSize:11, fontWeight:600, display:'inline-flex',
                    alignItems:'center', justifyContent:'center' }}>{i + 1}</span>
                  <span>{a.text}</span>
                </li>
              ))}
            </ol>
          ) : <div style={{ fontSize:12.5, color:'var(--text2)' }}>No agenda recorded.</div>}
        </Card>

        <Card>
          <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <b style={{ fontSize:14 }}>Meeting Notes</b>
            {canEdit && !editNotes && <button className="btn btn-secondary btn-sm" onClick={() => setEditNotes(true)}>Edit</button>}
          </div>
          {editNotes ? (
            <>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={8} style={{ width:'100%', fontSize:12.5 }} />
              <div className="flex-gap" style={{ gap:6, marginTop:6 }}>
                <button className="btn btn-primary btn-sm" onClick={saveNotes}>Save</button>
                <button className="btn btn-secondary btn-sm" onClick={() => { setEditNotes(false); setNotes(d.notes || ''); }}>Cancel</button>
              </div>
            </>
          ) : (
            <div style={{ background:'var(--bg3)', borderRadius:8, padding:11, fontSize:12.5,
              lineHeight:1.7, whiteSpace:'pre-wrap', minHeight:60, color: d.notes ? 'var(--text)' : 'var(--text2)' }}>
              {d.notes || 'No notes recorded.'}
            </div>
          )}
        </Card>
      </div>

      {/* ── Tabs + right rail ──────────────────────────────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'minmax(0,1fr) 300px', gap:12, alignItems:'start' }}>
        <div>
          <div style={{ display:'flex', gap:4, borderBottom:'1px solid var(--border)', marginBottom:0, flexWrap:'wrap' }}>
            {TABS.map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)}
                style={{ padding:'9px 14px', fontSize:12.5, cursor:'pointer', border:'none', background:'none',
                  color: tab === k ? 'var(--accent)' : 'var(--text2)', fontWeight: tab === k ? 700 : 400,
                  borderBottom: tab === k ? '2px solid var(--accent)' : '2px solid transparent' }}>{label}</button>
            ))}
          </div>

          <Card style={{ borderTopLeftRadius:0, borderTopRightRadius:0, borderTop:'none' }} pad={0}>
            {tab === 'points' && canEdit && (
              <div style={{ padding:'10px 12px', borderBottom:'1px solid var(--border)' }}>
                <button className="btn btn-primary btn-sm" onClick={() => setAddOpen(true)}>+ Add Discussion Point</button>
                <span style={{ fontSize:11.5, color:'var(--text2)', marginLeft:10 }}>
                  Task → a ticket per campus · Information → recorded only · Policy Change → acknowledgement per IT person
                </span>
              </div>
            )}
            <div className="table-wrap" style={{ overflowX:'auto' }}>
              {tab === 'points' && (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>
                    <th style={{ ...th, width:34 }}>#</th><th style={th}>Discussion Point</th>
                    <th style={th}>Category</th><th style={th}>Campus / Scope</th>
                    <th style={th}>Responsible</th><th style={th}>Due Date</th>
                    <th style={th}>Status</th><th style={th}>Linked Item</th><th style={th}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {items.map((it, i) => {
                      const c = CAT[it.category || 'task'] || CAT.task;
                      return (
                        <tr key={it.id}>
                          <td style={{ ...td, color:'var(--text2)' }}>{i + 1}</td>
                          <td style={{ ...td, fontWeight:500 }}>{it.title}</td>
                          <td style={td}><Pill text={c.label} bg={c.bg} fg={c.fg} /></td>
                          <td style={{ ...td, color:'var(--text2)' }}>{scopeOf(it)}</td>
                          <td style={td}>{responsibleOf(it)}</td>
                          <td style={td}>{dueOf(it) ? fmtDate(dueOf(it)) : '–'}</td>
                          <td style={td}><StatusPill s={pointStatus(it)} /></td>
                          <td style={{ ...td, color: linkOf(it) ? 'var(--accent)' : 'var(--text2)' }}>{linkOf(it) || '–'}</td>
                          <td style={td}>
                            <div className="flex-gap" style={{ gap:5 }}>
                              <button className="btn btn-secondary btn-sm" onClick={() => setView(it)}>View</button>
                              {canEdit && (
                                <button className="btn btn-secondary btn-sm" title={it.closed_at ? 'Reopen — it will carry forward again' : 'Close — stops it carrying forward'}
                                  onClick={() => closePoint(it, !it.closed_at)}>{it.closed_at ? '↩' : '✓'}</button>
                              )}
                              {canEdit && <button className="btn btn-secondary btn-sm" title="Delete" onClick={() => delPoint(it)}>🗑</button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!items.length && <tr><td colSpan={9} style={{ ...td, textAlign:'center', color:'var(--text2)', padding:22 }}>No discussion points recorded.</td></tr>}
                  </tbody>
                </table>
              )}

              {tab === 'tasks' && (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>
                    <th style={th}>Ticket</th><th style={th}>Task</th><th style={th}>From Point</th>
                    <th style={th}>Campus</th><th style={th}>Assigned To</th>
                    <th style={th}>Due Date</th><th style={th}>Status</th>
                  </tr></thead>
                  <tbody>
                    {items.flatMap(it => (it.tasks || []).map(t => (
                      <tr key={t.id}>
                        <td style={{ ...td, color:'var(--accent)', fontWeight:600 }}>{t.task_id}</td>
                        <td style={td}>{t.title}</td>
                        <td style={{ ...td, color:'var(--text2)' }}>{it.title}</td>
                        <td style={{ ...td, color:'var(--text2)' }}>{t.campus_name || '–'}</td>
                        <td style={td}>{t.assigned_to_name || '–'}</td>
                        <td style={td}>{t.due_date ? fmtDate(t.due_date) : '–'}</td>
                        <td style={td}><StatusPill s={t.status} /></td>
                      </tr>
                    )))}
                    {!allTasks.length && <tr><td colSpan={7} style={{ ...td, textAlign:'center', color:'var(--text2)', padding:22 }}>No tickets raised from this meeting yet.</td></tr>}
                  </tbody>
                </table>
              )}

              {tab === 'info' && (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>
                    <th style={{ ...th, width:34 }}>#</th><th style={th}>Information Point</th>
                    <th style={th}>Acknowledged</th><th style={th}>Still Pending</th><th style={th}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {infoPoints.map((it, i) => (
                      <tr key={it.id}>
                        <td style={{ ...td, color:'var(--text2)' }}>{i + 1}</td>
                        <td style={{ ...td, fontWeight:500 }}>{it.title}</td>
                        <td style={td}>{Number(it.acks_done || 0)} / {Number(it.acks_total || 0)}</td>
                        <td style={{ ...td, color:'var(--text2)' }}>{(it.pending_acks || []).join(', ') || '–'}</td>
                        <td style={td}><button className="btn btn-secondary btn-sm" onClick={() => setView(it)}>View</button></td>
                      </tr>
                    ))}
                    {!infoPoints.length && <tr><td colSpan={5} style={{ ...td, textAlign:'center', color:'var(--text2)', padding:22 }}>No information points.</td></tr>}
                  </tbody>
                </table>
              )}

              {tab === 'follow' && (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>
                    <th style={th}>From Meeting</th><th style={th}>Date</th><th style={th}>Point</th>
                    <th style={th}>Category</th><th style={th}>Still Pending Because</th><th style={th}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {carry.map(c => {
                      const cat = CAT[c.category || 'task'] || CAT.task;
                      return (
                        <tr key={c.id}>
                          <td style={{ ...td, color:'var(--text2)' }}>
                            {c.meeting_code ? <b>{c.meeting_code}</b> : null} {c.meeting_title || '–'}
                          </td>
                          <td style={{ ...td, color:'var(--text2)' }}>{fmtDate(c.meeting_date)}</td>
                          <td style={{ ...td, fontWeight:500 }}>{c.title}</td>
                          <td style={td}><Pill text={cat.label} bg={cat.bg} fg={cat.fg} /></td>
                          <td style={{ ...td, color:'#9A6B0B' }}>{c.pending_reason}</td>
                          <td style={td}>
                            {canEdit && (
                              <button className="btn btn-secondary btn-sm" title="Close it — it stops carrying forward"
                                onClick={() => closePoint(c, true)}>✓ Close</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {!carry.length && <tr><td colSpan={6} style={{ ...td, textAlign:'center', color:'var(--text2)', padding:22 }}>Nothing outstanding from earlier meetings.</td></tr>}
                  </tbody>
                </table>
              )}

              {tab === 'files' && (
                <>
                  {canEdit && (
                    <div style={{ padding:'10px 12px', borderBottom:'1px solid var(--border)' }}>
                      <label className="btn btn-secondary btn-sm" style={{ cursor:'pointer', display:'inline-block' }}>
                        ⬆ Attach a file
                        <input type="file" style={{ display:'none' }}
                          onChange={e => { upload(e.target.files[0]); e.target.value = ''; }} />
                      </label>
                      <span style={{ fontSize:11.5, color:'var(--text2)', marginLeft:10 }}>Up to 15 MB per file</span>
                    </div>
                  )}
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr>
                      <th style={th}>File Name</th><th style={th}>Uploaded By</th>
                      <th style={th}>Size</th><th style={th}>Date</th><th style={th}>Actions</th>
                    </tr></thead>
                    <tbody>
                      {(d.attachments || []).map(a => (
                        <tr key={a.id}>
                          <td style={{ ...td, fontWeight:500 }}>📄 {a.orig_name}</td>
                          <td style={{ ...td, color:'var(--text2)' }}>{a.uploaded_by_name || '–'}</td>
                          <td style={{ ...td, color:'var(--text2)' }}>{kb(Number(a.size_bytes))}</td>
                          <td style={{ ...td, color:'var(--text2)' }}>{fmtDate(String(a.created_at).slice(0, 10))}</td>
                          <td style={td}>
                            <div className="flex-gap" style={{ gap:6 }}>
                              <button className="btn btn-secondary btn-sm" onClick={() => download(a)}>⬇</button>
                              {canEdit && <button className="btn btn-secondary btn-sm" onClick={() => delAttachment(a)}>🗑</button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {!(d.attachments || []).length && <tr><td colSpan={5} style={{ ...td, textAlign:'center', color:'var(--text2)', padding:22 }}>No attachments.</td></tr>}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </Card>

          {/* ── Next actions ─────────────────────────────────────────────── */}
          <Card style={{ marginTop:12 }} pad={0}>
            <b style={{ fontSize:14, display:'block', padding:'11px 12px' }}>Next Actions</b>
            <div className="table-wrap" style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>
                  <th style={{ ...th, width:34 }}>#</th><th style={th}>Action</th>
                  <th style={th}>Responsible</th><th style={th}>Due Date</th><th style={th}>Status</th>
                </tr></thead>
                <tbody>
                  {nextActions.map((t, i) => (
                    <tr key={t.id}>
                      <td style={{ ...td, color:'var(--text2)' }}>{i + 1}</td>
                      <td style={td}>{t.title}</td>
                      <td style={td}>{t.assigned_to_name || '–'}</td>
                      <td style={td}>{t.due_date ? fmtDate(t.due_date) : '–'}</td>
                      <td style={td}><StatusPill s={t.status} /></td>
                    </tr>
                  ))}
                  {!nextActions.length && <tr><td colSpan={5} style={{ ...td, textAlign:'center', color:'var(--text2)', padding:20 }}>Nothing outstanding.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* ── Right rail ───────────────────────────────────────────────────── */}
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <Card>
            <b style={{ fontSize:14, display:'block', marginBottom:10 }}>Meeting Summary</b>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              {[['Total Points', summary.total, 'var(--text)', 'var(--bg3)'],
                ['Tasks', summary.tasks, '#C0392B', '#FDE2E4'],
                ['Information', summary.info, '#1F6FD0', '#DCE9FB'],
                ['Completed', summary.completed, '#1E7C4B', '#D7F2E1']].map(([label, n, fg, bg]) => (
                <div key={label} style={{ background:bg, borderRadius:8, padding:'9px 11px' }}>
                  <div style={{ fontSize:11, color:fg, fontWeight:600, marginBottom:2 }}>{label}</div>
                  <div style={{ fontSize:21, fontWeight:700, color:fg }}>{n}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <b style={{ fontSize:14, display:'block', marginBottom:10 }}>Task Status</b>
            <Donut total={allTasks.length} label="Tasks" slices={[
              { n: taskStat.completed, color:'#2FBF71', label:'Completed' },
              { n: taskStat.progress,  color:'#F0C245', label:'In Progress' },
              { n: taskStat.pending,   color:'#E5533D', label:'Pending' },
            ]} />
          </Card>

          <Card>
            <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'baseline', marginBottom:8 }}>
              <b style={{ fontSize:14 }}>Information Acknowledgement</b>
              <span style={{ fontSize:12, color:'var(--text2)' }}>{ack.done} / {ack.total}</span>
            </div>
            <div style={{ height:9, borderRadius:5, background:'var(--bg3)', overflow:'hidden' }}>
              <div style={{ width:`${ack.total ? Math.round(100 * ack.done / ack.total) : 0}%`,
                height:'100%', background:'var(--accent)' }} />
            </div>
            <div style={{ textAlign:'right', fontSize:11.5, color:'var(--text2)', marginTop:4 }}>
              {ack.total ? `${Math.round(100 * ack.done / ack.total)}%` : 'Nothing to acknowledge'}
            </div>
          </Card>
        </div>
      </div>

      {addOpen && <AddPoint containerId={id} campuses={campuses} itUsers={itUsers}
        onClose={() => setAddOpen(false)}
        onSaved={n => { setAddOpen(false); load(); flash(n ? `✓ Point added — ${n} created` : '✓ Point added'); }} />}
      {view && <PointModal it={view} d={d} itUsers={itUsers} campuses={campuses} canEdit={canEdit}
        pointStatus={pointStatus} isInfo={isInfo} onClose={() => setView(null)}
        onSaved={() => { setView(null); load(); }} flash={flash} />}
      {editOpen && <EditMeeting d={d} itUsers={itUsers} onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); load(); flash('✓ Meeting updated'); }} />}
    </div>
  );
}

// ── Donut ───────────────────────────────────────────────────────────────────
// Drawn with stroke-dasharray on concentric circles: no chart library, and it
// renders identically in both themes because every colour is explicit.
function Donut({ slices, total, label }) {
  const R = 52, C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <div className="flex-gap" style={{ gap:14, alignItems:'center', flexWrap:'wrap' }}>
      <svg width={124} height={124} viewBox="0 0 124 124" style={{ flexShrink:0 }}>
        <circle cx={62} cy={62} r={R} fill="none" stroke="var(--bg3)" strokeWidth={17} />
        {total > 0 && slices.filter(s => s.n > 0).map((s, i) => {
          const len = (s.n / total) * C;
          const el = <circle key={i} cx={62} cy={62} r={R} fill="none" stroke={s.color} strokeWidth={17}
            strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset}
            transform="rotate(-90 62 62)" />;
          offset += len;
          return el;
        })}
        <text x={62} y={58} textAnchor="middle" fontSize={22} fontWeight={700} fill="var(--text)">{total}</text>
        <text x={62} y={75} textAnchor="middle" fontSize={11} fill="var(--text2)">{label}</text>
      </svg>
      <div style={{ display:'flex', flexDirection:'column', gap:6, fontSize:12 }}>
        {slices.map(s => (
          <div key={s.label} className="flex-gap" style={{ gap:7, alignItems:'center' }}>
            <span style={{ width:10, height:10, borderRadius:3, background:s.color, flexShrink:0 }} />
            <span style={{ flex:1 }}>{s.label}</span>
            <span style={{ color:'var(--text2)' }}>{s.n} ({total ? Math.round(100 * s.n / total) : 0}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Add a discussion point ──────────────────────────────────────────────────
// The category decides what the point DOES, which is why it is picked first:
//   Task          → one ticket per selected campus, auto-assigned to its IT person
//   Information   → recorded only
//   Policy Change → an acknowledgement for each selected IT person
function AddPoint({ containerId, campuses, itUsers, onClose, onSaved }) {
  const [f, setF] = useState({ title:'', description:'', category:'task' });
  const [camp, setCamp] = useState({});
  const [users, setUsers] = useState({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.title.trim()) return setErr('Give the point a title');
    const campus_ids = Object.keys(camp).filter(k => camp[k]).map(Number);
    const user_ids   = Object.keys(users).filter(k => users[k]).map(Number);
    if (f.category === 'task' && !campus_ids.length)
      return setErr('Pick at least one campus — a Task point raises a ticket per campus');
    if ((f.category === 'policy_change' || f.category === 'information') && !user_ids.length)
      return setErr('Pick the IT people who must acknowledge this');
    setBusy(true);
    try {
      const r = await api.post(`/containers/${containerId}/items`,
        { ...f, title: f.title.trim(), campus_ids, user_ids });
      const n = Number(r.data.generated || 0);
      onSaved(n ? (f.category === 'task' ? `${n} ticket(s)` : `${n} acknowledgement(s)`) : '');
    } catch (e) { setErr(e.response?.data?.error || 'Could not add the point'); }
    finally { setBusy(false); }
  };

  const pickList = (list, state, setState, labelOf) => (
    <div style={{ display:'flex', flexWrap:'wrap', gap:8, maxHeight:150, overflowY:'auto',
      border:'1px solid var(--border)', borderRadius:8, padding:9 }}>
      {list.map(x => (
        <label className="opt" key={x.id}>
          <input type="checkbox" checked={!!state[x.id]}
            onChange={() => setState(s => ({ ...s, [x.id]: !s[x.id] }))} />
          <span>{labelOf(x)}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:620 }}>
        <div className="modal-header">
          <span className="modal-title">Add Discussion Point</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding:16 }}>
          {err && <div style={{ color:'var(--red)', fontSize:12.5, marginBottom:10 }}>{err}</div>}

          <div className="form-group"><label>Category</label>
            <select value={f.category} onChange={e => setF({ ...f, category:e.target.value })}>
              <option value="task">Task — raise a ticket per campus</option>
              <option value="information">Information — needs acknowledgement</option>
              <option value="policy_change">Policy Change — acknowledgement + added to the IT policy</option>
            </select>
          </div>
          <div className="form-group"><label>Discussion point</label>
            <input value={f.title} onChange={e => setF({ ...f, title:e.target.value })}
              placeholder="e.g. Pending ERP calls status review" />
          </div>
          <div className="form-group"><label>Detail</label>
            <textarea rows={3} value={f.description}
              onChange={e => setF({ ...f, description:e.target.value })}
              placeholder="What was discussed, and what needs doing" />
          </div>

          {f.category === 'task' && (
            <div className="form-group">
              <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center' }}>
                <label style={{ marginBottom:0 }}>Campuses — one ticket each, auto-assigned</label>
                <div className="flex-gap" style={{ gap:6 }}>
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => setCamp(Object.fromEntries(campuses.map(c => [c.id, true])))}>All {campuses.length}</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setCamp({})}>None</button>
                </div>
              </div>
              {pickList(campuses, camp, setCamp, c => c.name)}
            </div>
          )}
          {(f.category === 'policy_change' || f.category === 'information') && (
            <div className="form-group">
              <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center' }}>
                <label style={{ marginBottom:0 }}>IT people who must acknowledge</label>
                <div className="flex-gap" style={{ gap:6 }}>
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => setUsers(Object.fromEntries(itUsers.map(u => [u.id, true])))}>All</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setUsers({})}>None</button>
                </div>
              </div>
              {pickList(itUsers, users, setUsers, u => u.full_name)}
            </div>
          )}

          <div className="flex-gap" style={{ gap:8, marginTop:6 }}>
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>
              {busy ? 'Adding…' : '+ Add point'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── One discussion point ────────────────────────────────────────────────────
function PointModal({ it, itUsers, campuses, canEdit, pointStatus, isInfo, onClose, onSaved, flash }) {
  const [f, setF] = useState({
    responsible_user_id: it.responsible_user_id || '',
    due_date: it.due_date_str || '',
    point_status: it.point_status || '',
  });
  const [fan, setFan] = useState({});          // campuses to raise tickets for
  const [fanOpen, setFanOpen] = useState(false);
  const save = async () => {
    try { await api.patch(`/containers/items/${it.id}/point`, f); onSaved(); }
    catch (e) { flash(e.response?.data?.error || 'Could not save'); }
  };
  // Campuses that already have a ticket for this point are left out: fan-out
  // skips them anyway, and offering them suggests it would do something.
  const done = new Set((it.tasks || []).map(t => t.campus_id));
  const available = (campuses || []).filter(c => !done.has(c.id));
  const acknowledge = async () => {
    try { await api.post(`/containers/items/${it.id}/acknowledge`); flash('✓ Acknowledged'); onSaved(); }
    catch (e) { flash(e.response?.data?.error || 'Could not acknowledge'); }
  };
  const fanOut = async () => {
    const campus_ids = Object.keys(fan).filter(k => fan[k]).map(Number);
    if (!campus_ids.length) return flash('Pick at least one campus');
    try {
      const r = await api.post(`/containers/items/${it.id}/fanout`, { campus_ids });
      flash(`✓ ${r.data.created?.length ?? campus_ids.length} ticket(s) raised`);
      onSaved();
    } catch (e) { flash(e.response?.data?.error || 'Could not raise tickets'); }
  };
  const c = CAT[it.category || 'task'] || CAT.task;
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:620 }}>
        <div className="modal-header">
          <span className="modal-title">{it.title}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding:16 }}>
          <div className="flex-gap" style={{ gap:8, marginBottom:12 }}>
            <Pill text={c.label} bg={c.bg} fg={c.fg} />
            <StatusPill s={pointStatus(it)} />
          </div>
          {it.description && (
            <div style={{ background:'var(--bg3)', borderRadius:8, padding:11, fontSize:12.5,
              whiteSpace:'pre-wrap', marginBottom:14 }}>{it.description}</div>
          )}

          {(it.tasks || []).length > 0 && (
            <>
              <div style={{ fontSize:11.5, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', marginBottom:6 }}>Linked tickets</div>
              <div style={{ marginBottom:14 }}>
                {it.tasks.map(t => (
                  <div key={t.id} className="flex-gap" style={{ gap:8, alignItems:'center', padding:'5px 0',
                    borderBottom:'1px solid var(--border)', fontSize:12.5 }}>
                    <span style={{ color:'var(--accent)', fontWeight:600, minWidth:150 }}>{t.task_id}</span>
                    <span style={{ flex:1 }}>{t.title}</span>
                    <span style={{ color:'var(--text2)' }}>{t.assigned_to_name || '–'}</span>
                    <StatusPill s={t.status} />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Raise tickets for more campuses. Only a Task point generates them. */}
          {canEdit && !isInfo(it) && available.length > 0 && (
            <div style={{ marginBottom:14 }}>
              {!fanOpen
                ? <button className="btn btn-secondary btn-sm" onClick={() => setFanOpen(true)}>
                    + Raise tickets for {(it.tasks || []).length ? 'more ' : ''}campuses
                  </button>
                : <>
                    <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                      <div style={{ fontSize:11.5, fontWeight:600, color:'var(--text2)', textTransform:'uppercase' }}>
                        Raise a ticket for
                      </div>
                      <div className="flex-gap" style={{ gap:6 }}>
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => setFan(Object.fromEntries(available.map(c => [c.id, true])))}>All {available.length}</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setFan({})}>None</button>
                      </div>
                    </div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:8, maxHeight:130, overflowY:'auto',
                      border:'1px solid var(--border)', borderRadius:8, padding:9, marginBottom:8 }}>
                      {available.map(c => (
                        <label className="opt" key={c.id}>
                          <input type="checkbox" checked={!!fan[c.id]}
                            onChange={() => setFan(s => ({ ...s, [c.id]: !s[c.id] }))} />
                          <span>{c.name}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex-gap" style={{ gap:6 }}>
                      <button className="btn btn-primary btn-sm" onClick={fanOut}>Raise tickets</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setFanOpen(false); setFan({}); }}>Cancel</button>
                    </div>
                  </>}
            </div>
          )}

          {Number(it.acks_total) > 0 && (
            <div style={{ marginBottom:14, fontSize:12.5 }}>
              <b>Acknowledged {Number(it.acks_done)} / {Number(it.acks_total)}</b>
              {(it.pending_acks || []).length > 0 &&
                <div style={{ color:'var(--text2)', marginTop:3 }}>Still pending: {it.pending_acks.join(', ')}</div>}
            </div>
          )}

          {/* The person's own acknowledgement — this is the whole point of an
              Information or Policy Change entry, so it is a button, not a tick
              buried in a list. */}
          {it.my_pending_ack && (
            <div style={{ background:'#FDF0CD', border:'1px solid #E8CE86', borderRadius:8,
              padding:11, marginBottom:14 }}>
              <div style={{ fontSize:12.5, marginBottom:8, color:'#7A5300' }}>
                You have been asked to confirm you have read this.
              </div>
              <button className="btn btn-primary btn-sm" onClick={acknowledge}>✓ I have read and understood</button>
            </div>
          )}

          {canEdit && (
            <>
              <div style={{ fontSize:11.5, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', marginBottom:8 }}>
                Point tracking
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:10 }}>
                <div className="form-group"><label>Responsible</label>
                  <select value={f.responsible_user_id} onChange={e => setF({ ...f, responsible_user_id:e.target.value })}>
                    <option value="">— none —</option>
                    {itUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Due date</label>
                  <input type="date" value={f.due_date} onChange={e => setF({ ...f, due_date:e.target.value })} />
                </div>
                <div className="form-group"><label>Status</label>
                  <select value={f.point_status} onChange={e => setF({ ...f, point_status:e.target.value })}>
                    <option value="">— from its tickets —</option>
                    {['Pending','In Progress','Completed','Closed'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:2, marginBottom:12 }}>
                Leave status empty and it follows the linked tickets.
              </div>
              <div className="flex-gap" style={{ gap:8 }}>
                <button className="btn btn-primary btn-sm" onClick={save}>Save</button>
                <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Edit the meeting header ─────────────────────────────────────────────────
function EditMeeting({ d, itUsers, onClose, onSaved }) {
  const [f, setF] = useState({
    title: d.title || '', meeting_date: d.meeting_date_str || '',
    start_time: d.start_time_str || '', end_time: d.end_time_str || '',
    location: d.location || '', conducted_by_user_id: d.conducted_by_user_id || '',
    meeting_kind: d.meeting_kind || '', status: d.status || 'Open',
  });
  const [err, setErr] = useState('');
  const save = async () => {
    if (!f.title.trim()) return setErr('Title is required');
    try { await api.put(`/containers/${d.id}/meeting`, f); onSaved(); }
    catch (e) { setErr(e.response?.data?.error || 'Could not save'); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:560 }}>
        <div className="modal-header">
          <span className="modal-title">Edit Meeting</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding:16 }}>
          {err && <div style={{ color:'var(--red)', fontSize:12.5, marginBottom:10 }}>{err}</div>}
          <div className="form-group"><label>Title</label>
            <input value={f.title} onChange={e => setF({ ...f, title:e.target.value })} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px,1fr))', gap:10 }}>
            <div className="form-group"><label>Date</label>
              <input type="date" value={f.meeting_date} onChange={e => setF({ ...f, meeting_date:e.target.value })} />
            </div>
            <div className="form-group"><label>Start time</label>
              <input type="time" value={f.start_time} onChange={e => setF({ ...f, start_time:e.target.value })} />
            </div>
            <div className="form-group"><label>End time</label>
              <input type="time" value={f.end_time} onChange={e => setF({ ...f, end_time:e.target.value })} />
            </div>
          </div>
          <div className="form-group"><label>Location</label>
            <input value={f.location} onChange={e => setF({ ...f, location:e.target.value })}
              placeholder="Head Office — Conference Room" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:10 }}>
            <div className="form-group"><label>Conducted by</label>
              <select value={f.conducted_by_user_id} onChange={e => setF({ ...f, conducted_by_user_id:e.target.value })}>
                <option value="">— not set —</option>
                {itUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Meeting status</label>
              <select value={f.status} onChange={e => setF({ ...f, status:e.target.value })}>
                {['Open','Completed','Closed'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="flex-gap" style={{ gap:8, marginTop:6 }}>
            <button className="btn btn-primary btn-sm" onClick={save}>Save</button>
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
